/**
 * AI Service - Gemini integration
 * Handles natural language configuration commands
 */

import { GoogleGenerativeAI } from '@google/generative-ai';
import errorLog from './errorLog.js';
import { FOOTSWITCH_FUNCTIONS, ENCODER_FUNCTIONS } from '../constants/functions.js';

/**
 * Single source of truth for the AI model identity (id + human label).
 * VERIFIED 2026-08-25 against the live provider catalogue (OpenRouter model
 * list as proxy for the Gemini family): 'gemini-2.0-flash-exp' has been
 * retired; gemini-2.5-flash is the oldest current GA Flash model.
 */
export const AI_MODEL = {
    id: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash'
};

/**
 * Synonyms mapping spoken/common names to valid function VALUES.
 * Only used as a fallback; resolution always validates against the
 * constants (FOOTSWITCH_FUNCTIONS / ENCODER_FUNCTIONS) before emitting an op.
 */
const FUNCTION_SYNONYMS = {
    mute: 'mute_current_track',
    'mute current': 'mute_current_track',
    'mute current track': 'mute_current_track',
    solo: 'solo_current_track',
    'solo current track': 'solo_current_track',
    record: 'rec_play',
    rec: 'rec_play',
    'rec/play': 'rec_play',
    play: 'rec_play',
    overdub: 'rec_play',
    stop: 'stop',
    'stop all': 'stop_all',
    clear: 'clear',
    undo: 'undo_redo',
    redo: 'undo_redo',
    'undo/redo': 'undo_redo',
    reverse: 'reverse',
    fade: 'fade',
    speed: 'speed',
    'swap focus': 'change_track_focus',
    'change focus': 'change_track_focus',
    'track focus': 'change_track_focus',
    // Per-track function synonyms (resolve to real FOOTSWITCH_FUNCTIONS values)
    'mute track a': 'mute_track_a',
    'mute track b': 'mute_track_b',
    'solo track a': 'solo_track_a',
    'solo track b': 'solo_track_b',
    'select track a': 'select_track_a',
    'select track b': 'select_track_b',
    'stop b': 'stop_b',
    'record b': 'rec_play_b',
    'rec b': 'rec_play_b',
    // Loop slot management synonyms
    'load next': 'load_next_slot',
    'load next slot': 'load_next_slot',
    'load previous': 'load_prev_slot',
    'load prev': 'load_prev_slot',
    'load previous slot': 'load_prev_slot',
    'load prev slot': 'load_prev_slot',
    // Encoder-oriented synonyms
    volume: 'volume',
    'main volume': 'volume',
    'fade time': 'fade_time',
    crossfade: 'crossfade',
    'menu navigation': 'menu_navigate'
};

/**
 * Normalise a spoken/raw function name to a string key.
 */
function _normaliseName(raw) {
    return String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, ' ')
        .replace(/[^a-z0-9_ /]/g, '');
}

/**
 * Resolve a spoken function name to a VALID value from the provided
 * list of constant entries, using direct value/label match then synonyms.
 * Returns the resolved value string, or null if it is not a real value.
 */
function _resolveFunction(raw, validList) {
    const cleaned = _normaliseName(raw);
    if (!cleaned) return null;

    const validValues = validList.map(f => f.value);

    // 1) Direct match against the underscore value (allow spaces for underscores).
    const asValue = cleaned.replace(/ /g, '_');
    if (validValues.includes(asValue)) return asValue;
    if (validValues.includes(cleaned)) return cleaned;

    // 2) Match against a function's human label (lowercased).
    const byLabel = validList.find(
        f => _normaliseName(f.label) === cleaned
    );
    if (byLabel) return byLabel.value;

    // 3) Synonym table, then re-validate the mapped value.
    const mapped = FUNCTION_SYNONYMS[cleaned];
    if (mapped && validValues.includes(mapped)) return mapped;

    return null;
}

/**
 * PURE parser: turn AI/user text into an array of structured, validated
 * configuration ops. No side effects, safe to unit-test.
 *
 * Supported ops:
 *   { kind: 'footswitch', target: 'fs1'|'fs2'|'ext1'|'ext2', function: '<valid value>' }
 *   { kind: 'midiChannel', value: <1-16> }
 *   { kind: 'encoder', function: '<valid value>' }
 *
 * Ops are only emitted when the resolved function is a real value in the
 * relevant constants (FOOTSWITCH_FUNCTIONS / ENCODER_FUNCTIONS) and the
 * MIDI channel is within 1-16.
 *
 * @param {string} text
 * @returns {Array<object>}
 */
export function parseConfigCommands(text) {
    const ops = [];
    if (!text || typeof text !== 'string') return ops;

    // Footswitch: "set footswitch 1 to mute", "set fs2 to stop",
    // "set ext1 to clear", "set external footswitch 2 to mute current track".
    const fsPattern =
        /set\s+(?:the\s+)?(?:(footswitch|fs)\s*([12])|(external(?:\s+footswitch)?|ext)\s*([12]))\s+to\s+([a-z0-9_ /]+?)(?=\s+and\s+|[.,;!\n]|$)/gi;
    for (const m of text.matchAll(fsPattern)) {
        let target;
        if (m[2]) {
            target = 'fs' + m[2];
        } else if (m[4]) {
            target = 'ext' + m[4];
        }
        const fn = _resolveFunction(m[5], FOOTSWITCH_FUNCTIONS);
        if (target && fn) {
            ops.push({ kind: 'footswitch', target, function: fn });
        }
    }

    // MIDI channel: "set midi channel to 5".
    const chPattern = /set\s+(?:the\s+)?midi\s+channel\s+to\s+(\d{1,2})/gi;
    for (const m of text.matchAll(chPattern)) {
        const value = parseInt(m[1], 10);
        if (Number.isInteger(value) && value >= 1 && value <= 16) {
            ops.push({ kind: 'midiChannel', value });
        }
    }

    // Encoder: "set encoder to volume", "set the encoder to fade time".
    const encPattern =
        /set\s+(?:the\s+)?encoder\s+to\s+([a-z0-9_ /]+?)(?=\s+and\s+|[.,;!\n]|$)/gi;
    for (const m of text.matchAll(encPattern)) {
        const fn = _resolveFunction(m[1], ENCODER_FUNCTIONS);
        if (fn) {
            ops.push({ kind: 'encoder', function: fn });
        }
    }

    return ops;
}

class AIService {
    constructor() {
        this.genAI = null;
        this.model = null;
        this.maxRetries = 2; // As specified in Gemini.md
    }

    /**
     * Initialize Gemini AI
     */
    initialize(apiKey) {
        if (!apiKey) {
            throw new Error('Gemini API key is required');
        }

        try {
            this.genAI = new GoogleGenerativeAI(apiKey);
            this.model = this.genAI.getGenerativeModel({
                model: AI_MODEL.id // single-sourced; see AI_MODEL above
            });
            console.log(`✅ Gemini AI initialized (${AI_MODEL.id})`);
            return true;
        } catch (error) {
            console.error('❌ Gemini initialization failed:', error);
            errorLog.capture(error, 'ai', { op: 'initialize' });
            throw error;
        }
    }

    /**
     * Send message to AI with retry logic
     */
    async sendMessage(userMessage, currentProfile = null) {
        if (!this.model) {
            throw new Error('AI not initialized. Please provide API key.');
        }

        // Build context-aware prompt (rebuilt from scratch each call — the
        // profile is the live context; no server-side chat history is kept).
        const systemContext = this._buildSystemContext(currentProfile);
        const fullPrompt = `${systemContext}\n\nUser: ${userMessage}`;

        // Retry logic (2 retries)
        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                const result = await this.model.generateContent(fullPrompt);
                const response = result.response;
                const text = response.text();

                console.log('✅ AI response received');
                return {
                    success: true,
                    response: text,
                    suggestions: this._extractSuggestions(text)
                };

            } catch (error) {
                console.error(`AI request attempt ${attempt + 1} failed:`, error);
                errorLog.capture(error, 'ai', { op: 'sendMessage' });

                if (attempt === this.maxRetries) {
                    // Explicit, user-facing AI/model error so the chat clearly
                    // signals this is a model failure (unavailable / bad key / quota),
                    // not a generic app error. Mentions the model id for context.
                    return {
                        success: false,
                        error: `AI request failed (model ${AI_MODEL.id}) after ${this.maxRetries + 1} attempts. The model may be unavailable, your API key may be invalid, or you may have hit a quota limit. Details: ${error.message}`
                    };
                }

                // Wait 1 second before retry
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    /**
     * Build system context from Gemini.md and current profile
     */
    _buildSystemContext(currentProfile) {
        let context = `You are an AI assistant for the Ed Sheeran Looper Plus MIDI control application.

HARDWARE SPECS:
- 2 built-in footswitches (FS1, FS2)
- Optional external footswitch (EXT1, EXT2 - can be 0, 1, or 2 pedals)
- Large encoder knob (assignable)
- 128 program slots for saved loops
- WAV audio files only (44.1/48kHz, 16/24-bit)

YOUR ROLE:
- Help users configure their Looper Plus
- Explain MIDI commands
- Suggest optimal configurations
- Parse natural language into configuration changes

TONE: Helpful, professional, musician-friendly. Use terms like "loop", "overdub", "verse", "chorus".

CURRENT CONFIGURATION:`;

        if (currentProfile && currentProfile.footswitches) {
            const fs = currentProfile.footswitches;
            context += `
Profile: ${currentProfile.name || 'Unnamed'}
- FS1: ${fs.fs1?.function ?? 'unknown'}
- FS2: ${fs.fs2?.function ?? 'unknown'}
- EXT1: ${fs.ext1?.enabled ? (fs.ext1.function ?? 'unknown') : 'Disabled'}
- EXT2: ${fs.ext2?.enabled ? (fs.ext2.function ?? 'unknown') : 'Disabled'}
- Encoder: ${currentProfile.encoder?.function ?? 'unknown'}
- MIDI Channel: ${currentProfile.midiSettings?.channel ?? 1}
- Loop Sync: ${currentProfile.trackSettings?.mode ?? 'unknown'}`;
        } else {
            context += '\nNo profile loaded (using defaults)';
        }

        return context;
    }

    /**
     * Extract configuration suggestions from AI response.
     * Delegates to the pure parseConfigCommands() so suggestions reflect
     * the same validated ops the UI can apply.
     */
    _extractSuggestions(text) {
        return parseConfigCommands(text);
    }

    /**
     * Clear conversation history
     * (Kept for the UI's Clear button; the service itself keeps no history —
     * each prompt is rebuilt from the live profile.)
     */
    clearHistory() {
        console.log('🗑️ Conversation history cleared');
    }

    /**
     * Check if AI is initialized
     */
    isInitialized() {
        return this.model !== null;
    }
}

export const aiService = new AIService();
export default aiService;
